import { useLazyQuery, useMutation } from "@apollo/client";
import { compact, flatMap, omit } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AssessmentQuestionResponses,
  AssessmentType,
  ComplianceProgramForReportAssessmentGroupSectionFragment,
  EngagementSurveyReportGroupBy,
  GetComplianceProgramForReportQuery,
  GetComplianceProgramForReportQueryVariables,
  GetComplianceProgramReportingViewsQuery,
  GetComplianceProgramReportingViewsQueryVariables,
  GetPerformanceAssessmentChartLabelsQuery,
  GetPerformanceAssessmentChartLabelsQueryVariables,
  OverallGoalAssessmentQuestionNode,
  OverallRecognitionCoreValueAssessmentQuestionNode,
  SaveComplianceProgramReportingViewMutation,
  SaveComplianceProgramReportingViewMutationVariables,
  TrashComplianceProgramReportingViewMutation,
  TrashComplianceProgramReportingViewMutationVariables,
} from "types/graphql-schema";

import createOrUpdateComplianceProgramReportingViewMutation from "@apps/reporting/graphql/create-or-update-compliance-program-reporting-view-mutation";
import deleteComplianceProgramReportingViewMutation from "@apps/reporting/graphql/delete-compliance-program-reporting-view-mutation";
import getComplianceProgramForReportQuery from "@apps/reporting/graphql/get-compliance-program-for-report-query";
import getComplianceProgramReportingViews from "@apps/reporting/graphql/get-compliance-program-reporting-views";
import getPerformanceAssessmentChartLabelsQuery from "@apps/reporting/graphql/get-performance-assessment-chart-labels-query";
import { successNotificationVar } from "@cache/cache";
import { ComboboxGenericOption } from "@components/combobox/generic-combobox";
import { assertEdgesNonNull, assertNonNull } from "@helpers/helpers";
import useConfirm from "@helpers/hooks/use-confirm";

import {
  initAssessmentReportViewOptions,
  isAssessmentReportQuestionType,
} from "../helpers";
import { ChartLabel } from "./nine-box/types";
import { AssessmentReportViewOptions } from "./report-filters/types";

const useAssessmentReport = (
  assessmentType: AssessmentType,
  complianceProgramId: number
) => {
  const [showViewOptionsSidebar, setShowViewOptionsSidebar] = useState(false);
  const [chartLabels, setChartLabels] = useState<ChartLabel[]>([]);
  const [baselineViewOptions, setBaselineViewOptions] =
    useState<AssessmentReportViewOptions>(initAssessmentReportViewOptions);
  const [viewOptions, setViewOptions] = useState<AssessmentReportViewOptions>(
    initAssessmentReportViewOptions
  );

  const handleHideViewOptionsSidebar = useCallback(() => {
    setShowViewOptionsSidebar(false);
  }, []);
  const handleShowViewOptionsSidebar = useCallback(() => {
    setShowViewOptionsSidebar(true);
  }, []);
  const handleUpdateViewOptions = useCallback(
    (opts: Partial<AssessmentReportViewOptions>) => {
      setViewOptions((prev) => ({
        ...prev,
        isDirty: true,
        ...opts,
      }));
    },
    []
  );
  const handleResetViewOptionsToBaseline = useCallback(() => {
    setViewOptions(baselineViewOptions);
  }, [baselineViewOptions]);
  const handleResetViewOptionsToEmpty = useCallback(() => {
    setBaselineViewOptions(initAssessmentReportViewOptions);
    setViewOptions(initAssessmentReportViewOptions);
  }, []);

  const [getReport, { data: programData, loading: isLoadingProgram }] =
    useLazyQuery<
      GetComplianceProgramForReportQuery,
      GetComplianceProgramForReportQueryVariables
    >(getComplianceProgramForReportQuery, {});

  const complianceProgram = useMemo(
    () => (programData ? programData?.complianceProgram : null),
    [programData]
  );

  const assessmentTemplate = useMemo(() => {
    if (!complianceProgram) {
      return null;
    }
    return assessmentType === AssessmentType.Performance
      ? complianceProgram.performanceAssessmentTemplate
      : complianceProgram.managerAssessmentTemplate;
  }, [assessmentType, complianceProgram]);

  const questions = useMemo(() => {
    if (!assessmentTemplate) {
      return [];
    }
    const overallGoalQuestion = assessmentTemplate.additionalQuestions.find(
      (q) => q && q.__typename === "OverallGoalAssessmentQuestionNode"
    ) as OverallGoalAssessmentQuestionNode | undefined;
    const overallValuesQuestion = assessmentTemplate.additionalQuestions.find(
      (q) =>
        q &&
        q.__typename === "OverallRecognitionCoreValueAssessmentQuestionNode"
    ) as OverallRecognitionCoreValueAssessmentQuestionNode | undefined;

    const questionSets = assertEdgesNonNull(assessmentTemplate.questionSets);
    const sections = questionSets.reduce((acc, set) => {
      return [...acc, ...assertEdgesNonNull(set.sections)];
    }, [] as ComplianceProgramForReportAssessmentGroupSectionFragment[]);
    const allQuestions = flatMap(sections, (section) =>
      assertEdgesNonNull(section.questions)
    );
    return compact([
      ...allQuestions.map((question) =>
        isAssessmentReportQuestionType(question.question) &&
        question.question.responses &&
        [
          AssessmentQuestionResponses.Both,
          AssessmentQuestionResponses.ExcludeSelfAssessment,
        ].includes(question.question.responses)
          ? question.question
          : null
      ),
      overallGoalQuestion,
      overallValuesQuestion,
    ]);
  }, [assessmentTemplate]);
  const talentIndicatorQuestions = useMemo(() => {
    return questions.filter(
      (question) =>
        question.__typename === "TalentIndicatorAssessmentQuestionNode"
    );
  }, [questions]);

  const [getChartLabels] = useLazyQuery<
    GetPerformanceAssessmentChartLabelsQuery,
    GetPerformanceAssessmentChartLabelsQueryVariables
  >(getPerformanceAssessmentChartLabelsQuery, {
    variables: {
      questionIds: questions.map((q) => q.id),
    },
    onCompleted: (data) => {
      setChartLabels(
        data?.performanceAssessmentChartLabels
          ? assertEdgesNonNull(data.performanceAssessmentChartLabels)
          : []
      );
    },
  });
  const [getReportingViews, { data: reportingViewsData }] = useLazyQuery<
    GetComplianceProgramReportingViewsQuery,
    GetComplianceProgramReportingViewsQueryVariables
  >(getComplianceProgramReportingViews);
  const [saveReportingView, { loading: isSavingReportingView }] = useMutation<
    SaveComplianceProgramReportingViewMutation,
    SaveComplianceProgramReportingViewMutationVariables
  >(createOrUpdateComplianceProgramReportingViewMutation);
  const [deleteReportingView, { loading: isDeletingReportingView }] =
    useMutation<
      TrashComplianceProgramReportingViewMutation,
      TrashComplianceProgramReportingViewMutationVariables
    >(deleteComplianceProgramReportingViewMutation);

  const handleSaveReportingView = useCallback(
    (opts: AssessmentReportViewOptions) => {
      saveReportingView({
        variables: {
          complianceProgramId,
          assessmentType,
          viewOptions: JSON.stringify(omit(opts, "id", "name", "isDirty")),
          id: opts.id,
          name: opts.name,
        },
        refetchQueries: [getComplianceProgramReportingViews],
        onCompleted: (response) => {
          const newViewOptionsData = assertNonNull(
            response.createOrUpdateComplianceProgramReportingView
              ?.complianceProgramReportingView
          );
          successNotificationVar({
            title: "View saved successfully",
            timeout: 2000,
          });
          handleResetViewOptionsToBaseline();
          const newViewOptions = {
            id: newViewOptionsData?.id,
            name: newViewOptionsData?.name,
            isDirty: false,
            ...JSON.parse(newViewOptionsData?.viewOptions),
          };
          handleUpdateViewOptions(newViewOptions);
          setBaselineViewOptions(newViewOptions);
        },
      });
    },
    [
      saveReportingView,
      complianceProgramId,
      assessmentType,
      handleResetViewOptionsToBaseline,
      handleUpdateViewOptions,
    ]
  );

  const handleDuplicateView = useCallback(() => {
    handleSaveReportingView({
      ...baselineViewOptions,
      id: undefined,
      name:
        viewOptions.name === baselineViewOptions.name
          ? `Copy of ${viewOptions.name}`
          : viewOptions.name.trim() === ""
          ? "New View"
          : viewOptions.name,
    });
  }, [baselineViewOptions, handleSaveReportingView, viewOptions.name]);

  const handleSaveAsNewView = useCallback(() => {
    handleSaveReportingView({
      ...viewOptions,
      id: undefined,
      name:
        viewOptions.name === baselineViewOptions.name
          ? `Copy of ${viewOptions.name}`
          : viewOptions.name.trim() === ""
          ? "New View"
          : viewOptions.name,
    });
  }, [baselineViewOptions, handleSaveReportingView, viewOptions]);

  const {
    ConfirmationDialog: ReportingViewDeletionConfirm,
    confirm: confirmDeleteReportingView,
  } = useConfirm(
    "Are you sure?",
    "Are you sure you want to delete this reporting view?"
  );

  const handleSaveExistingView = useCallback(() => {
    handleSaveReportingView({
      ...viewOptions,
      name: viewOptions.name.trim() === "" ? "New View" : viewOptions.name,
    });
  }, [handleSaveReportingView, viewOptions]);

  const handleDeleteView = useCallback(async () => {
    if (!viewOptions.id) {
      return;
    }
    const confirmation = await confirmDeleteReportingView();
    if (!confirmation) {
      return;
    }

    deleteReportingView({
      variables: {
        id: viewOptions.id,
      },
      refetchQueries: [getComplianceProgramReportingViews],
      onCompleted: () => {
        successNotificationVar({
          title: "View deleted successfully",
          timeout: 2000,
        });
        handleResetViewOptionsToEmpty();
      },
    });
  }, [
    deleteReportingView,
    handleResetViewOptionsToEmpty,
    viewOptions.id,
    confirmDeleteReportingView,
  ]);

  useEffect(() => {
    handleResetViewOptionsToEmpty();
    getReport({
      variables: {
        complianceProgramId,
        isPerformanceAssessmentReport:
          assessmentType === AssessmentType.Performance,
        isUpwardAssessmentReport: assessmentType === AssessmentType.Manager,
        isEngagementSurveyReport: false,
        engagementSurveyReportGroupBy: EngagementSurveyReportGroupBy.Team,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complianceProgramId]);
  useEffect(() => {
    if (complianceProgram) {
      getReportingViews({
        variables: {
          complianceProgramId: complianceProgram.id,
          assessmentType,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complianceProgram]);
  useEffect(() => {
    if (questions.length > 0) {
      getChartLabels({
        variables: {
          questionIds: questions.map((q) => q.id),
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions]);

  const savedViewOptions = useMemo(() => {
    return reportingViewsData?.complianceProgramReportingViews
      ? assertEdgesNonNull(
          reportingViewsData.complianceProgramReportingViews
        ).map((opt) => ({
          value: opt.id,
          label: opt.name ?? "",
        }))
      : [];
  }, [reportingViewsData]);
  const handleChangeSavedView = useCallback(
    (opt: ComboboxGenericOption<number>) => {
      if (!reportingViewsData?.complianceProgramReportingViews) {
        return;
      }
      const newViewOptionsData = assertEdgesNonNull(
        reportingViewsData.complianceProgramReportingViews
      ).find((view) => view.id === opt.value);
      const newViewOptions = {
        id: newViewOptionsData?.id,
        name: newViewOptionsData?.name ?? "",
        isDirty: false,
        ...JSON.parse(newViewOptionsData?.viewOptions),
      };
      handleResetViewOptionsToBaseline();
      handleUpdateViewOptions(newViewOptions);
      setBaselineViewOptions(newViewOptions);
    },
    [
      handleResetViewOptionsToBaseline,
      handleUpdateViewOptions,
      reportingViewsData,
    ]
  );

  return {
    chartLabels,
    setChartLabels,
    viewOptions,
    showViewOptionsSidebar,
    handleHideViewOptionsSidebar,
    handleShowViewOptionsSidebar,
    handleUpdateViewOptions,
    handleResetViewOptionsToBaseline,
    handleSaveAsNewView,
    handleSaveExistingView,
    handleDuplicateView,
    handleDeleteView,
    complianceProgram,
    isLoadingProgram,
    assessmentTemplate,
    questions,
    talentIndicatorQuestions,
    isSavingReportingView,
    isDeletingReportingView,
    ReportingViewDeletionConfirm,
    savedViewOptions,
    handleChangeSavedView,
  };
};

export default useAssessmentReport;
